import React, { useEffect, useState } from "react";
import { Button, Space, Divider, Spin } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
  LanguageCard,
  ComputerSkillCard,
  CourseSeminarCard,
  CertificateCard,
  ExamCard,
} from "./CompetencyCards";
import {
  ForeignLanguageModal,
  ComputerKnowledgeModal,
  CourseSeminarModal,
  CertificateModal,
  ExamModal,
} from "./CompetenciesModals";
import useCommonsService from "../../../../services/useCommonsService";
import usePersonnelInfoService from "../../../../services/usePersonnelInfoService";
import {
  EditCertificateModal,
  EditComputerKnowledgeModal,
  EditCourseSeminarModal,
  EditExamModal,
  EditForeignLanguageModal,
} from "./EditCompetenciesModals";

const Competencies = ({ personnelInfoData }) => {
  const {
    useForeignLanguageLearnedPlatforms,
    useEducationTypes,
    useKnowledgeLevels,
    useGetAllForeignLanguages,
    useGetAllComputerInformations,
    useYearsOfExperiences,
  } = useCommonsService();
  const { data: foreignLanguagePlatforms, isLoading: isLanguagesLoading } =
    useForeignLanguageLearnedPlatforms();
  const { data: knowledgeLevels, isLoading: isKnowledgeLevelsLoading } =
    useKnowledgeLevels();
  const { data: educationTypes, isLoading: isEducationTypesLoading } =
    useEducationTypes();
  const { data: foreignLanguages, isLoading: isForeignLanguagesLoading } =
    useGetAllForeignLanguages();
  const {
    data: computerInformationOptions,
    isLoading: isComputerInformationsLoading,
  } = useGetAllComputerInformations();
  const {
    data: yearsOfExperienceOptions,
    isLoading: isYearsOfExperienceOptionsLoading,
  } = useYearsOfExperiences();

  const {
    useAddPersonnelCourse,
    useAddPersonnelSertificate,
    useAddPersonnelExam,
    useAddPersonnelForeignLanguage,
    useAddPersonnelComputerInformation,
  } = usePersonnelInfoService();
  const { mutateAsync: addPersonelCourse, isPending: isAddingCourse } =
    useAddPersonnelCourse();
  const {
    mutateAsync: addPersonnelSertificate,
    isPending: isAddingCertificate,
  } = useAddPersonnelSertificate();
  const { mutateAsync: addPersonnelExam, isPending: isAddingExam } =
    useAddPersonnelExam();
  const {
    mutateAsync: addPersonnelForeignLanguage,
    isPending: isAddingForeignLanguage,
  } = useAddPersonnelForeignLanguage();
  const {
    mutateAsync: addPersonnelComputerInformation,
    isPending: isAddingComputerInfo,
  } = useAddPersonnelComputerInformation();

  const [modalVisible, setModalVisible] = useState({
    languages: false,
    computerSkills: false,
    coursesAndSeminars: false,
    certificates: false,
    exams: false,
  });
  const [editModalVisible, setEditModalVisible] = useState({
    languages: false,
    computerSkills: false,
    coursesAndSeminars: false,
    certificates: false,
    exams: false,
  });
  const [editingItem, setEditingItem] = useState(null);

  const [competencies, setCompetencies] = useState({
    languages: [],
    computerSkills: [],
    coursesAndSeminars: [],
    certificates: [],
    exams: [],
  });

  useEffect(() => {
    if (!personnelInfoData) {
      return;
    }
    const transformedData = {
      languages: personnelInfoData.personnelForeignLanguages,
      computerSkills: personnelInfoData.personnelComputerInformations,
      coursesAndSeminars: personnelInfoData.personnelCourses,
      certificates: personnelInfoData.personnelCertificates,
      exams: personnelInfoData.personnelExams,
    };
    setCompetencies(transformedData);
  }, [personnelInfoData]);

  const handleModalCancel = (type) => {
    setModalVisible({ ...modalVisible, [type]: false });
    setEditingItem(null);
  };

  const handleEditModalCancel = (type) => {
    setEditModalVisible({ ...editModalVisible, [type]: false });
    setEditingItem(null);
  };

  const handleEdit = (type, item) => {
    setEditingItem(item);
    setEditModalVisible({ ...editModalVisible, [type]: true });
  };

  const handleAddCourse = async (values) => {
    const requestBody = {
      ...values,
      month: values.date.$M + 1,
      year: values.date.$y,
    };
    try {
      await addPersonelCourse(requestBody);
      setModalVisible({ coursesAndSeminars: false });
    } catch (error) {}
  };

  const handleAddSertificate = async (values) => {
    const requestBody = {
      ...values,
      month: values.date.$M + 1,
      year: values.date.$y,
    };
    try {
      await addPersonnelSertificate(requestBody);
      setModalVisible({ certificates: false });
    } catch (error) {}
  };

  const handleAddForeignLanguage = async (values) => {
    const requestBody = {
      ...values,
    };
    try {
      await addPersonnelForeignLanguage(requestBody);
      setModalVisible({ languages: false });
    } catch (error) {}
  };

  const handleAddComputerInformation = async (values) => {
    const requestBody = {
      ...values,
    };
    try {
      await addPersonnelComputerInformation(requestBody);
      setModalVisible({ languages: false });
    } catch (error) {}
  };

  const handleAddExam = async (values) => {
    const requestBody = {
      ...values,
      month: values.date.$M + 1,
      year: values.date.$y,
    };
    try {
      await addPersonnelExam(requestBody);
      setModalVisible({ exams: false });
    } catch (error) {}
  };

  const modals = {
    languages: (
      <>
        <ForeignLanguageModal
          visible={modalVisible.languages}
          onCancel={() => handleModalCancel("languages")}
          onSave={(values) => handleAddForeignLanguage(values)}
          foreignLanguageLearnedPlatformOptions={foreignLanguagePlatforms}
          foreingLanguages={foreignLanguages}
          knowledgeLevelOptions={knowledgeLevels}
          isSubmitting={isAddingForeignLanguage}
        />
        <EditForeignLanguageModal
          visible={editModalVisible.languages}
          onCancel={() => handleEditModalCancel("languages")}
          onSave={(values) => handleAddForeignLanguage(values)}
          initialValues={editingItem}
          foreignLanguageLearnedPlatformOptions={foreignLanguagePlatforms}
          foreingLanguages={foreignLanguages}
          knowledgeLevelOptions={knowledgeLevels}
        />
      </>
    ),
    computerSkills: (
      <>
        <ComputerKnowledgeModal
          visible={modalVisible.computerSkills}
          onCancel={() => handleModalCancel("computerSkills")}
          onSave={(values) => handleAddComputerInformation(values)}
          knowledgeLevelOptions={knowledgeLevels}
          computerInformationOptions={computerInformationOptions}
          yearsOfExperienceOptions={yearsOfExperienceOptions}
          isSubmitting={isAddingComputerInfo}
        />
        <EditComputerKnowledgeModal
          visible={editModalVisible.computerSkills}
          onCancel={() => handleEditModalCancel("computerSkills")}
          initialValues={editingItem}
          knowledgeLevelOptions={knowledgeLevels}
          computerInformationOptions={computerInformationOptions}
          yearsOfExperienceOptions={yearsOfExperienceOptions}
        />
      </>
    ),
    coursesAndSeminars: (
      <>
        <CourseSeminarModal
          visible={modalVisible.coursesAndSeminars}
          onCancel={() => handleModalCancel("coursesAndSeminars")}
          onSave={(values) => handleAddCourse(values)}
          isSubmitting={isAddingCourse}
        />
        <EditCourseSeminarModal
          visible={editModalVisible.coursesAndSeminars}
          onCancel={() => handleEditModalCancel("coursesAndSeminars")}
          initialValues={editingItem}
        />
      </>
    ),
    certificates: (
      <>
        <CertificateModal
          visible={modalVisible.certificates}
          onCancel={() => handleModalCancel("certificates")}
          onSave={(values) => handleAddSertificate(values)}
          isSubmitting={isAddingCertificate}
        />
        <EditCertificateModal
          visible={editModalVisible.certificates}
          onCancel={() => handleEditModalCancel("certificates")}
          initialValues={editingItem}
        />
      </>
    ),
    exams: (
      <>
        <ExamModal
          visible={modalVisible.exams}
          onCancel={() => handleModalCancel("exams")}
          onSave={(values) => handleAddExam(values)}
          isSubmitting={isAddingExam}
        />
        <EditExamModal
          visible={editModalVisible.exams}
          onCancel={() => handleEditModalCancel("exams")}
          initialValues={editingItem}
        />
      </>
    ),
  };

  const competencySections = [
    {
      key: "languages",
      title: "Yabancı Diller",
      buttonText: "Yabancı Dil Ekle",
      Component: LanguageCard,
    },
    {
      key: "computerSkills",
      title: "Bilgisayar Bilgileri",
      buttonText: "Bilgisayar Bilgisi Ekle",
      Component: ComputerSkillCard,
    },
    {
      key: "coursesAndSeminars",
      title: "Kurslar/Seminerler",
      buttonText: "Kurs Seminer Ekle",
      Component: CourseSeminarCard,
    },
    {
      key: "certificates",
      title: "Sertifikalar",
      buttonText: "Sertifika Ekle",
      Component: CertificateCard,
    },
    {
      key: "exams",
      title: "Sınavlar",
      buttonText: "Sınav Ekle",
      Component: ExamCard,
    },
  ];

  return (
    <>
      {/* <Spin> */}
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Space wrap style={{ width: "100%", justifyContent: "flex-start" }}>
          {competencySections.map((section) => (
            <Button
              key={section.key}
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingItem(null);
                setModalVisible({ ...modalVisible, [section.key]: true });
              }}>
              {section.buttonText}
            </Button>
          ))}
        </Space>
        <Divider style={{ margin: "12px 0" }} />
        {competencySections.map((section) => (
          <section.Component
            key={section.key}
            data={competencies[section.key]}
            onEdit={(item) => handleEdit(section.key, item)}
          />
        ))}
      </Space>

      {Object.values(modals)}
      {/* </Spin> */}
    </>
  );
};

export default Competencies;
